/**
 * Primary role assignment — `users/{uid}.primaryRoleId` + seed `role_progress` on pick.
 */

import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { ensureFirestoreAuthReady } from '../utils/firestoreAuthReady';
import { loadRole } from './rolesService';
import { createDefaultRoleProgress } from './roleProgressService';

/**
 * Create `users/{uid}/role_progress/{roleId}` at Level 1 when missing.
 * @param {string} userId
 * @param {string} roleId
 * @returns {Promise<boolean>} true when a new doc was created
 */
export async function initializeRoleProgressIfMissing(userId, roleId) {
  const uid = userId != null ? String(userId).trim() : '';
  const rid = roleId != null ? String(roleId).trim() : '';
  if (!uid || !rid) return false;

  const ref = doc(db, 'users', uid, 'role_progress', rid);
  const snap = await getDoc(ref);
  if (snap.exists()) return false;

  const defaults = createDefaultRoleProgress(rid);
  await setDoc(ref, {
    level: defaults.level,
    current_exp: defaults.current_exp,
    exp_to_level: defaults.exp_to_level,
    updatedAt: serverTimestamp()
  });

  return true;
}

/**
 * Set the user's primary learning role and ensure `role_progress` exists.
 * @param {string} userId
 * @param {string} roleId
 * @returns {Promise<{ primaryRoleId: string }>}
 */
export async function assignPrimaryRole(userId, roleId) {
  const uid = userId != null ? String(userId).trim() : '';
  const rid = roleId != null ? String(roleId).trim() : '';
  if (!uid || !rid) {
    throw new Error('roleAssignmentService: userId and roleId are required');
  }

  const role = await loadRole(rid);
  if (!role) {
    throw new Error(`roleAssignmentService: role "${rid}" is not published`);
  }

  await initializeRoleProgressIfMissing(uid, rid);

  const userRef = doc(db, 'users', uid);
  await setDoc(
    userRef,
    {
      primaryRoleId: rid,
      primaryRoleUpdatedAt: serverTimestamp()
    },
    { merge: true }
  );

  return { primaryRoleId: rid };
}

/**
 * Read `primaryRoleId` from the user document.
 * @param {string|null|undefined} userId
 * @returns {Promise<string|null>}
 */
export async function loadPrimaryRoleId(userId) {
  const uid = userId != null ? String(userId).trim() : '';
  if (!uid) return null;

  try {
    const authReady = await ensureFirestoreAuthReady(uid);
    if (!authReady) return null;

    const snap = await getDoc(doc(db, 'users', uid));
    if (!snap.exists()) return null;
    const value = snap.data()?.primaryRoleId;
    return value != null && String(value).trim() ? String(value).trim() : null;
  } catch (err) {
    console.warn('roleAssignmentService: loadPrimaryRoleId failed', err);
    return null;
  }
}
